import { useEffect, useRef, useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type SavedView } from "@features/metrics-explorer/types";
import {
  GET_METRICS_EXPLORER_SAVED_VIEWS,
  UPDATE_METRICS_EXPLORER_SAVED_VIEW,
} from "@gql/metrics-explorer-saved-views";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type UpdateMetricsExplorerSavedView,
  type UpdateMetricsExplorerSavedViewVariables,
} from "@north-beam/nb-client-side";
import { TertiaryButton } from "@shared/buttons";

import { SavedViewsMenuLoadingRow } from "./saved-views-menu-loading-row";

interface UpdateSavedViewsProps {
  savedView: SavedView;
  isUpdating: boolean;
  setIsUpdating(value: boolean): void;
}

export const UpdateSavedViews = ({
  savedView,
  isUpdating,
  setIsUpdating,
}: UpdateSavedViewsProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const inputRef = useRef<HTMLInputElement>(null);

  const [localValue, setLocalValue] = useState(savedView.name);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const [updateSavedViewMutation] = useNorthbeamMutation<
    UpdateMetricsExplorerSavedView,
    UpdateMetricsExplorerSavedViewVariables
  >(UPDATE_METRICS_EXPLORER_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_METRICS_EXPLORER_SAVED_VIEWS }],
  });

  const updateSavedView = async () => {
    prettyLogEventPage({
      domain: "Metrics Explorer",
      action: "onClick",
      additionalActionInfo: "Update Saved View Name",
      data: {
        pageContext: { savedView },
      },
    });

    try {
      setIsUpdateLoading(true);
      await updateSavedViewMutation({
        variables: { id: savedView.id, name: localValue },
      });
      setIsUpdating(false);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (isUpdating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isUpdating]);

  if (!isUpdating) {
    return null;
  }

  return (
    <div className="text-xs w-full h-full flex items-center bg-inherit z-1">
      <SavedViewsMenuLoadingRow isLoading={isUpdateLoading} text="Updating...">
        <div className="border-b border-cosmic flex items-center w-full">
          <input
            ref={inputRef}
            className="flex-1 text-sm h-full bg-inherit font-normal w-full"
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          <TertiaryButton
            className="hover:bg-transparent"
            onClick={updateSavedView}
          >
            SAVE
          </TertiaryButton>
        </div>
      </SavedViewsMenuLoadingRow>
    </div>
  );
};
